import { useMemo, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@course-authoring/shared-types';
import type { TagSummary, TagUsage } from '../../api/tagApi';
import { useTagPickerDropdown } from './useTagPickerDropdown';
import { isNearMatch } from './tagFuzzyMatch';

export interface TagPickerViewProps {
  tags: TagSummary[];
  allTags: TagUsage[];
  /** Which per-type count to show in the dropdown — a lesson picker shows lessonCount, a course picker courseCount, never the combined totalCount. */
  countKey: 'lessonCount' | 'courseCount';
  onAssign: (tagId: number) => void;
  onUnassign: (tagId: number) => void;
  disabled?: boolean;
  /**
   * Open the dropdown whenever the input gains focus (default). A host that
   * moves focus into the input programmatically — the Edit tags dialog — turns
   * this off so the list doesn't open over its own buttons; click, typing, and
   * ArrowDown still open it.
   */
  openOnFocus?: boolean;
}

type DropdownMode = 'browse' | 'narrowed' | 'didYouMean' | 'appliedMatch' | 'noMatch';

export function TagPickerView({
  tags,
  allTags,
  countKey,
  onAssign,
  onUnassign,
  disabled = false,
  openOnFocus = true,
}: TagPickerViewProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const [query, setQuery] = useState('');
  const { isOpen, openDropdown, closeDropdown, highlightedIndex, setHighlightedIndex, containerRef, inputRef } =
    useTagPickerDropdown();

  const appliedIds = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);
  const available = useMemo(() => allTags.filter((tag) => !appliedIds.has(tag.id)), [allTags, appliedIds]);

  const trimmedQuery = query.trim();
  const directMatches = useMemo(() => {
    if (!trimmedQuery) return available;
    const needle = trimmedQuery.toLowerCase();
    return available.filter((tag) => tag.name.toLowerCase().includes(needle));
  }, [available, trimmedQuery]);

  // "Did you mean" only kicks in once a direct/substring search comes up
  // empty — it's a fallback for typos, not a second opinion alongside real matches.
  const nearMatches = useMemo(() => {
    if (!trimmedQuery || directMatches.length > 0) return [];
    return available.filter((tag) => isNearMatch(trimmedQuery, tag.name));
  }, [available, directMatches.length, trimmedQuery]);

  // Reaching for a tag that's already on this item isn't "no match" — name it.
  const appliedMatch = useMemo(() => {
    if (!trimmedQuery) return undefined;
    const needle = trimmedQuery.toLowerCase();
    return tags.find((tag) => tag.name.toLowerCase() === needle);
  }, [tags, trimmedQuery]);

  const mode: DropdownMode = !trimmedQuery
    ? 'browse'
    : directMatches.length > 0
      ? 'narrowed'
      : nearMatches.length > 0
        ? 'didYouMean'
        : appliedMatch
          ? 'appliedMatch'
          : 'noMatch';

  const options = mode === 'browse' || mode === 'narrowed' ? directMatches : mode === 'didYouMean' ? nearMatches : [];

  const handleSelect = (tagId: number) => {
    onAssign(tagId);
    setQuery('');
    // Refocus first, then close: clicking an option moves focus to the option
    // button, so focusing the input here fires its onFocus (openDropdown) and
    // would otherwise reopen the list we are about to close.
    inputRef.current?.focus();
    closeDropdown();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) { openDropdown(); return; }
      setHighlightedIndex((index) => (options.length === 0 ? 0 : (index + 1) % options.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) { openDropdown(); return; }
      setHighlightedIndex((index) => (options.length === 0 ? 0 : (index - 1 + options.length) % options.length));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const highlighted = options[highlightedIndex];
      if (highlighted) handleSelect(highlighted.id);
    } else if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault();
        // Stop here so an enclosing dialog's Escape-to-close doesn't also fire;
        // a second Escape (dropdown already closed) is left to propagate.
        event.stopPropagation();
        closeDropdown();
      }
    } else if (event.key === 'Backspace' && query === '' && tags.length > 0) {
      const lastTag = tags[tags.length - 1];
      if (lastTag) {
        onUnassign(lastTag.id);
        closeDropdown();
      }
    }
  };

  return (
    <div style={styles.field}>
      <span style={styles.label}>Tags</span>
      <div ref={containerRef} style={styles.pickerContainer}>
        <div style={{ ...styles.chipRow, ...(isOpen ? styles.chipRowFocused : {}) }}>
          {tags.map((tag) => (
            <span key={tag.id} style={styles.chip}>
              {tag.name}
              <button
                type="button"
                onClick={() => onUnassign(tag.id)}
                disabled={disabled}
                style={styles.chipRemoveButton}
                aria-label={`Remove ${tag.name} tag`}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setHighlightedIndex(0);
              if (!isOpen) openDropdown();
            }}
            onFocus={openOnFocus ? openDropdown : undefined}
            // The input keeps focus after a selection, so onFocus won't fire
            // again; a click is how the author reopens the closed list.
            onClick={() => { if (!isOpen) openDropdown(); }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={tags.length > 0 ? 'Add another tag...' : 'Add a tag...'}
            style={styles.input}
            aria-label="Add a tag"
          />
        </div>

        {isOpen && !disabled && (
          <div style={styles.dropdown} role="listbox">
            {(mode === 'browse' || mode === 'narrowed') && (
              <>
                <div style={styles.dropdownHeading}>ALL TAGS</div>
                {options.length === 0 ? (
                  <div style={styles.dropdownEmpty}>No tags left to add.</div>
                ) : (
                  options.map((tag, index) => (
                    <button
                      type="button"
                      key={tag.id}
                      role="option"
                      aria-selected={index === highlightedIndex}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(tag.id)}
                      style={{
                        ...styles.dropdownOption,
                        ...(index === highlightedIndex ? styles.dropdownOptionActive : {}),
                      }}
                    >
                      <span>{tag.name}</span>
                      <span style={styles.dropdownOptionUsage}>{tag[countKey]} uses</span>
                    </button>
                  ))
                )}
              </>
            )}

            {mode === 'didYouMean' && (
              <>
                <div style={styles.dropdownHeading}>DID YOU MEAN</div>
                {options.map((tag, index) => (
                  <button
                    type="button"
                    key={tag.id}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelect(tag.id)}
                    style={{
                      ...styles.dropdownOption,
                      ...(index === highlightedIndex ? styles.dropdownOptionActive : {}),
                    }}
                  >
                    <span>{tag.name}</span>
                    <span style={styles.dropdownOptionUsage}>{tag[countKey]} uses</span>
                  </button>
                ))}
              </>
            )}

            {mode === 'appliedMatch' && appliedMatch && (
              <div style={styles.dropdownEmpty}>
                &quot;{appliedMatch.name}&quot; is already applied to this item.
              </div>
            )}

            {mode === 'noMatch' && (
              <div style={styles.dropdownEmpty}>
                No tags match. The tag list is managed centrally — new tags can&apos;t be added here.
              </div>
            )}
          </div>
        )}
      </div>
      <p style={styles.helperText}>Used to group and filter content across the library.</p>
    </div>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  return {
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xs,
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    pickerContainer: {
      position: 'relative',
    },
    chipRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: tokens.spacing.xs,
      padding: tokens.spacing.sm,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.backgrounds.page,
    },
    // Applied on top of chipRow while the dropdown is open — same role as a
    // native input's focus ring, since the chips+input row is what visually
    // reads as "the field" here.
    chipRowFocused: {
      border: `2px solid ${tokens.ui.focusRing}`,
      padding: `calc(${tokens.spacing.sm} - 1px)`,
    },
    // Soft pill, matching the WorkflowStatusBadge convention (light tint +
    // text-colored label) rather than a solid brand fill — a filled chip per
    // tag reads as louder than this field warrants next to Title/Description.
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 8px',
      borderRadius: '999px',
      backgroundColor: '#ECECF7',
      color: tokens.text.primary,
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
    chipRemoveButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      padding: 0,
      fontSize: '11px',
    },
    input: {
      flex: '1 1 120px',
      minWidth: '120px',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      fontSize: '14px',
      color: tokens.text.primary,
    },
    dropdown: {
      position: 'absolute',
      top: 'calc(100% + 4px)',
      left: 0,
      right: 0,
      maxHeight: '260px',
      overflowY: 'auto',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      backgroundColor: tokens.backgrounds.page,
      boxShadow: tokens.shadow.sm,
      zIndex: 20,
    },
    dropdownHeading: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: tokens.text.muted,
    },
    dropdownOption: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      color: tokens.text.primary,
      textAlign: 'left',
    },
    // Same soft lavender as the applied-tag chip, not a gray hover tint — the
    // highlighted row should read as "this is a tag" rather than a generic
    // list hover state.
    dropdownOptionActive: {
      backgroundColor: '#ECECF7',
    },
    dropdownOptionUsage: {
      fontSize: '12px',
      color: tokens.text.muted,
    },
    dropdownEmpty: {
      padding: tokens.spacing.sm,
      fontSize: '13px',
      color: tokens.text.muted,
    },
    helperText: {
      margin: 0,
      fontSize: '12px',
      color: tokens.text.muted,
    },
  };
}
